/**
 * Panel contract and shared DOM helpers.
 *
 * Every panel is a factory returning `{ root, update(state) }`. It receives an
 * `issue` callback and NOTHING else — no state object it could mutate, no
 * reference to the simulation. Spec §11's boundary is that the UI reads
 * snapshots and issues commands, and this shape is what makes writing a panel
 * that violates it awkward rather than easy.
 *
 * `update` is called with a fresh snapshot whenever state changes. Panels
 * mutate their own DOM in place rather than rebuilding it: rebuilding would
 * destroy focus, scroll position, and any in-progress drag every tick.
 *
 * A panel has NO CHROME. It used to carry move-up, move-down and collapse
 * buttons, which earned their place when eleven panels shared one scrolling
 * column and the player needed to bring the useful ones to the top. The console
 * groups panels onto screens instead, so there is nothing left to rearrange —
 * and at the touch size every control now has to clear, three of them ate
 * 180 pixels of header on every panel to solve a problem that no longer exists.
 */

import type { Command } from '../../sim/commands';
import type { GameState } from '../../sim/types';

export type Issue = (command: Command) => void;

export interface Panel {
  root: HTMLElement;
  update(state: GameState): void;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A titled panel shell. */
export function section(title: string): { root: HTMLDivElement; body: HTMLDivElement } {
  const root = el('div', 'panel');
  // Not read by the layout any more, but it names the panel in the inspector
  // and in any test that needs to find one.
  root.dataset['panel'] = title;

  const heading = el('div', 'panel-title');
  heading.append(el('span', 'panel-title-text', title));

  const body = el('div', 'panel-body');
  root.append(heading, body);
  return { root, body };
}

/**
 * Compact number formatting. The inventory shows 27 counters that change every
 * tick, and six-digit raw numbers make the column jump around as they grow.
 */
export function compact(value: number): string {
  if (value < 1000) return String(value);
  if (value < 1_000_000) {
    const thousands = value / 1000;
    return `${thousands < 10 ? thousands.toFixed(1) : Math.round(thousands)}k`;
  }
  return `${(value / 1_000_000).toFixed(1)}M`;
}

/** Set textContent only when it actually differs, to avoid needless reflow. */
export function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}
